import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { SunDoodle, Squiggle, WhatsAppIcon } from "./Illustrations";
import { SITE, whatsappLink } from "../data/site";

export default function Footer() {
  return (
    <footer className="relative bg-[#F3EADA] text-[#2C2C2A] mt-24" data-testid="site-footer">
      {/* Newsletter card */}
      <div className="mx-auto max-w-[1280px] px-5 md:px-10 pt-20 md:pt-28 pb-12">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-end">
          <div>
            <p className="eyebrow mb-3">Stay in the sun</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
              New drops,<br />
              small shop notes,<br />
              <span className="italic">no spam.</span>
            </h2>
          </div>
          <NewsletterForm />
        </div>
      </div>

      <Squiggle w={140} className="mx-auto opacity-50" />
      <FooterLinks />
      <FooterBottom />
    </footer>
  );
}

async function submitNewsletter(e) {
  e.preventDefault();
  const form = e.currentTarget;
  const f = new FormData(form);
  const email = String(f.get("email") || "");
  const status = form.querySelector("[data-testid='newsletter-status']");
  if (status) status.textContent = "Sending…";
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, source: "footer" }),
    });
    const data = await res.json();
    if (res.ok && data?.ok) {
      if (status) status.textContent = "You're in. Welcome to SOL Kids. ☀️";
      form.reset();
    } else {
      if (status) status.textContent = data?.detail || "Hmm, that didn't look right. Try again?";
    }
  } catch (err) {
    console.error("Newsletter subscribe failed", err);
    if (status) status.textContent = "Network blip — try again in a moment.";
  }
}

function NewsletterForm() {
  return (
    <form className="flex flex-col gap-3" onSubmit={submitNewsletter} data-testid="newsletter-form">
      <label className="eyebrow text-[#595955]">Email</label>
      <div className="flex items-center gap-2 border-b border-[#2C2C2A]/30 pb-3">
        <input
          type="email" required name="email" placeholder="your@inbox.com"
          className="flex-1 bg-transparent outline-none font-sans text-lg md:text-xl placeholder:text-[#8A8A85]"
          data-testid="newsletter-email"
        />
        <button type="submit" className="font-medium underline-offset-4 hover:underline" data-testid="newsletter-submit">Subscribe →</button>
      </div>
      <p className="text-xs text-[#595955] mt-2">By subscribing, you agree to occasional warm letters.</p>
      <p className="text-xs mt-1 min-h-[16px]" data-testid="newsletter-status" aria-live="polite"></p>
    </form>
  );
}

function FooterLinks() {
  return (
    <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-14 grid md:grid-cols-4 gap-10 md:gap-6 text-[14px]">
      <div className="col-span-2 md:col-span-1">
        <Link to="/" className="inline-flex items-center mb-4">
          <img src="/logo-trim.png" alt="SOL Kids" className="h-16 w-auto" draggable="false" />
        </Link>
        <p className="text-[#595955] leading-relaxed max-w-[260px]">
          Polarized sunglasses, built for little adventures. Made in India, with love.
        </p>
      </div>
      <div>
        <p className="eyebrow mb-4">Shop</p>
        <ul className="space-y-2.5">
          <li><Link to="/story" className="ink-link">Our story</Link></li>
          <li><Link to="/collections" className="ink-link">Collection</Link></li>
          <li><Link to="/personalities" className="ink-link">By personality</Link></li>
          <li><Link to="/quiz" className="ink-link">Find their pair</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-4">Brand</p>
        <ul className="space-y-2.5">
          <li><Link to="/why-sol" className="ink-link">Why SOL</Link></li>
          <li><Link to="/faq" className="ink-link">FAQ</Link></li>
          <li><Link to="/wishlist" className="ink-link">Wishlist</Link></li>
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-4">Reach us</p>
        <ul className="space-y-2.5">
          <li>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 ink-link">
              <WhatsAppIcon size={14} /> WhatsApp +91 98308 04015
            </a>
          </li>
          <li>
            <a href={SITE.instagramUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 ink-link">
              <Instagram className="w-3.5 h-3.5" /> {SITE.instagram}
            </a>
          </li>
          <li><a href={`mailto:${SITE.email}`} className="ink-link">{SITE.email}</a></li>
        </ul>
      </div>
    </div>
  );
}

function FooterBottom() {
  return (
    <div className="border-t border-[#2C2C2A]/10">
      <div className="mx-auto max-w-[1280px] px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-[#595955]">
        <p>© {new Date().getFullYear()} SOL Kids — All rights reserved.</p>
        <p>Made with <span style={{ color: "#EE8C72" }}>♥</span> in India, for little adventurers.</p>
      </div>
    </div>
  );
}
